import "./transactions.css";
import {
  profilePurpleIcons,
  profileFilledDarkIcons,
} from "../../utils/globalImages";
import {
  truncateText,
  formatDateTime,
  copyToClipboard,
  shortenTextWithReadMore,
} from "../../utils/HelperFunctions";
import { useSelector } from "react-redux";
import { Eye, Search, X } from "lucide-react";
import Toaster from "../../components/Toast/Toast";
import { useEffect, useRef, useState } from "react";
import { Tooltip, OverlayTrigger } from "react-bootstrap";
import { GetTransaction } from "../../api/modules/b2droppie.class";
import Pagination from "../../components/Pagination/Pagination";
import BackdropLoader from "../../components/BackdropLoader/BackdropLoader";

const GameHistory = () => {
  const sectionTopRef = useRef(null);
  const isCopyingRef = useRef(false);
  const isSortingRef = useRef(false);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("created");
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchOpen, setSearchOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const isDarkMode = useSelector((state) => state.theme.darkMode);

  // Drag functionality states
  const [dragPosition, setDragPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);
  const modalRef = useRef(null);

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchTransactions(currentPage, search, sortBy, sortOrder);
    }, 400);
    return () => clearTimeout(delay);
  }, [currentPage, search, sortBy, sortOrder]);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showModal]);

  const fetchTransactions = async (
    page = 1,
    searchValue = search,
    sortField = sortBy,
    sortDirection = sortOrder,
    gameHistory = "true",
  ) => {
    if (!isSortingRef.current) {
      setLoading(true);
    }
    try {
      const res = await GetTransaction(
        page,
        searchValue,
        sortField,
        sortDirection,
        gameHistory,
      );
      setTransactions(res?.data?.data?.data || []);
      setCurrentPage(res?.data?.data?.current_page || 1);
      setLastPage(res?.data?.data?.last_page || 1);
    } catch (err) {
      console.error("Error fetching transactions:", err);
    } finally {
      setLoading(false);
      isSortingRef.current = false;
    }
  };

  const handleSort = (field) => {
    isSortingRef.current = true;
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return "⇅";
    return sortOrder === "asc" ? "↑" : "↓";
  };

  const handleViewProducts = (item) => {
    setSelectedProducts(item);
    setShowModal(true);
    setDragPosition(0);
  };

  const statusBadge = (status) => {
    const normalized = status?.toString()?.trim()?.toUpperCase();
    const className = normalized?.toLowerCase()?.replace(/\s+/g, "_");
    return (
      <span
        className={`text-AktivGrotesk-Regular custom-status-badge ${className}`}
      >
        {normalized?.toLowerCase()}
      </span>
    );
  };

  const handleTouchStart = (e) => {
    startY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    if (diff > 0) {
      setDragPosition(diff);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (dragPosition > 100) {
      setShowModal(false);
      setTimeout(() => setDragPosition(0), 300);
    } else {
      setDragPosition(0);
    }
  };

  const copyText = async (text) => {
    if (isCopyingRef.current) return;
    isCopyingRef.current = true;
    await copyToClipboard(text, {
      onSuccess: (msg) => setToast({ success: msg }),
      onError: (msg) => setToast({ error: msg }),
    });
    setTimeout(() => {
      isCopyingRef.current = false;
    }, 1000);
  };

  const isBattle = selectedProducts?.type === "Battle";
  const isMobile = window.innerWidth <= 768;

  const renderModalBody = () => {
    if (isBattle) {
      return (
        <>
          {/* Desktop Battle Table */}
          <div className="desktop-table-view">
            <table
              className={`products-table ${isDarkMode ? "products-table-dark" : ""}`}
            >
              <thead>
                <tr>
                  <th>Winnings</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {selectedProducts?.winnings
                      ? `$${selectedProducts?.winnings}`
                      : "—"}
                  </td>
                  <td>
                    <span
                      className={`custom-status-badge ${
                        selectedProducts?.battle_won
                          ? "battle-won"
                          : "battle-lost"
                      }`}
                    >
                      {selectedProducts?.battle_won ? "WON" : "LOST"}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile Battle Cards */}
          <div className="mobile-cards-view">
            <div className="mobile-product-card" style={{ marginTop: "8px" }}>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "2px" }}
              >
                <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                  Winnings
                </span>
                <span style={{ fontSize: "13px", fontWeight: 600 }}>
                  {selectedProducts?.winnings
                    ? `$${selectedProducts?.winnings}`
                    : "—"}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                  alignItems: "center",
                }}
              >
                {" "}
                <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                  Result
                </span>
                <span
                  className={`custom-status-badge ${
                    selectedProducts?.battle_won ? "battle-won" : "battle-lost"
                  }`}
                >
                  {selectedProducts?.battle_won ? "WON" : "LOST"}
                </span>
              </div>
            </div>
          </div>
        </>
      );
    }

    if (selectedProducts?.products && selectedProducts.products.length > 0) {
      return (
        <>
          {/* Desktop Products Table */}
          <div className="desktop-table-view">
            <table
              className={`products-table ${isDarkMode ? "products-table-dark" : ""}`}
            >
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Description</th>
                  <th>Quantity</th>
                  <th>Color</th>
                  <th>Price</th>
                  {selectedProducts?.type === "Unboxing" &&
                    selectedProducts?.spin_result && (
                      <>
                        <th>Server Seed</th>
                        <th>Client Seed</th>
                        <th>RNG Outcome</th>
                        <th>Nonce</th>
                      </>
                    )}
                </tr>
              </thead>
              <tbody>
                {selectedProducts.products.map((product, i) => (
                  <tr key={i}>
                    <td>
                      <div className="product-cell">
                        <img
                          alt={product.name}
                          src={product.image}
                          className="product-image"
                        />
                        <span
                          className={`${isDarkMode ? "" : "text-charcoal-gray"} product-name mt-0`}
                        >
                          {product.name}
                        </span>
                      </div>
                    </td>
                    <td
                      className="description-cell"
                      dangerouslySetInnerHTML={{
                        __html: shortenTextWithReadMore(
                          product.description,
                          120,
                        ),
                      }}
                    />
                    <td>{product.quantity}</td>
                    <td>
                      <div className="color-cell">
                        <div
                          className="color-circle"
                          style={{ backgroundColor: product.product_color }}
                        ></div>
                      </div>
                    </td>
                    <td className="price-cell">${product.price}</td>
                    {selectedProducts?.type === "Unboxing" &&
                      selectedProducts?.spin_result && (
                        <>
                          <td>
                            <div className="d-start gap-10px">
                              {truncateText(
                                selectedProducts?.spin_result.server_seed,
                                10,
                              )}
                              <OverlayTrigger
                                placement="top"
                                container={document.body}
                                trigger={["hover", "focus"]}
                                overlay={<Tooltip>Click to Copy</Tooltip>}
                              >
                                <span
                                  onClick={() =>
                                    copyText(
                                      selectedProducts?.spin_result
                                        ?.server_seed,
                                    )
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <i
                                    className="fa-solid fa-copy"
                                    style={{
                                      color: isDarkMode ? "#9ecfff" : "#007bff",
                                    }}
                                  ></i>
                                </span>
                              </OverlayTrigger>
                            </div>
                          </td>
                          <td>
                            <div className="d-start gap-10px">
                              {truncateText(
                                selectedProducts?.spin_result.client_seed,
                                10,
                              )}
                              <OverlayTrigger
                                placement="top"
                                container={document.body}
                                trigger={["hover", "focus"]}
                                overlay={<Tooltip>Click to Copy</Tooltip>}
                              >
                                <span
                                  onClick={() =>
                                    copyText(
                                      selectedProducts?.spin_result
                                        ?.client_seed,
                                    )
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <i
                                    className="fa-solid fa-copy"
                                    style={{
                                      color: isDarkMode ? "#9ecfff" : "#007bff",
                                    }}
                                  ></i>
                                </span>
                              </OverlayTrigger>
                            </div>
                          </td>
                          <td>{selectedProducts?.spin_result.rng_outcome}</td>
                          <td>{selectedProducts?.spin_result.nonce}</td>
                        </>
                      )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Products Cards */}
          <div className="mobile-cards-view">
            <div
              className="mobile-header-row"
              style={{
                display: "grid",
                gridTemplateColumns: "1.5fr 2fr 1fr",
                padding: "8px 12px",
                fontSize: "12px",
                color: "#9ca3af",
              }}
            >
              <span>Product</span>
              <span>Description</span>
              <span style={{ textAlign: "right" }}>Price</span>
            </div>

            {selectedProducts.products.map((product, i) => (
              <div key={i} className="mobile-product-card">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    minWidth: 0,
                  }}
                >
                  <img
                    alt={product.name}
                    src={product.image}
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "6px",
                      objectFit: "cover",
                    }}
                  />
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {product.name}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "12px",
                    color: "#9ca3af",
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {product.description}
                </span>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    textAlign: "right",
                  }}
                >
                  ${product.price}
                </span>
              </div>
            ))}
          </div>
        </>
      );
    }

    return (
      <p className="no-products-text">
        No products available for this transaction.
      </p>
    );
  };

  return (
    <>
      <Toaster message={toast} />
      <div
        ref={sectionTopRef}
        className={`transactions-header d-space-between ${isDarkMode ? "dark" : "light"}`}
      >
        <div className="d-space-between w-100">
          <div
            className={`d-start gap-16px ${searchOpen ? "hide-on-mobile-screen" : ""}`}
          >
            <div className="transactions-icon">
              <img
                src={
                  isDarkMode
                    ? profileFilledDarkIcons.gameHistoryFilledDark
                    : profilePurpleIcons.gameHistoryFilledPurple
                }
                alt="exchange-icon"
              />
            </div>
            <div className="transactions-header-text">
              <h1
                className={`transactions-title-header ${
                  isDarkMode ? "text-white" : "text-deep-purple"
                }`}
              >
                Game History
              </h1>
              <p className="transactions-subtitle-header text-light-gray">
                View your game history
              </p>
            </div>
          </div>
          <div className={`search-wrapper ${searchOpen ? "open" : ""}`}>
            <input
              type="text"
              value={search}
              placeholder="Search"
              className="search-input"
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              onBlur={() => {
                if (!search) {
                  setTimeout(() => setSearchOpen(false), 200);
                }
              }}
            />
            <div
              className="search-toggle"
              onClick={() => {
                setSearchOpen(!searchOpen);
                if (!searchOpen) {
                  setTimeout(() => {
                    document.querySelector(".search-input")?.focus();
                  }, 300);
                }
              }}
            >
              {searchOpen ? <X size={18} /> : <Search size={18} />}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <BackdropLoader show={loading} />
      ) : (
        <div
          className={`myaccount-table-container ${
            isDarkMode ? "dark" : "light"
          }`}
        >
          <div className="myaccount-table-wrapper">
            <table className="profile-tables">
              <thead>
                <tr>
                  <th className="ps-4">ID/Name</th>
                  <th onClick={() => handleSort("amount")} className="sortable">
                    Amount {getSortIcon("amount")}
                  </th>
                  <th onClick={() => handleSort("type")} className="sortable">
                    Type {getSortIcon("type")}
                  </th>
                  <th
                    onClick={() => handleSort("currency")}
                    className="sortable"
                  >
                    Currency {getSortIcon("currency")}
                  </th>
                  <th
                    onClick={() => handleSort("closing_balance")}
                    className="sortable"
                  >
                    Closing Balance {getSortIcon("closing_balance")}
                  </th>
                  <th
                    onClick={() => handleSort("created")}
                    className="sortable"
                  >
                    Created at {getSortIcon("created")}
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length > 0 ? (
                  transactions.map((item, index) => (
                    <tr key={index} className="withdrawal-row">
                      <td>
                        <b>{item?.transaction_id || ""}</b>
                        {"  "}
                        {item?.box_name
                          ? item.box_name
                          : item?.products?.length > 0
                            ? item.products
                                .map((p) => `${p?.name?.split(" ")[0]}...`)
                                .join(", ")
                            : ""}
                      </td>
                      <td>{item?.amount ? `${item?.amount}` : "N/A"}</td>
                      <td>{statusBadge(item?.type) || "N/A"}</td>
                      <td>{item?.currency || "USD"}</td>
                      <td className="tablehead">
                        {item?.closing_balance
                          ? `${item?.closing_balance}`
                          : "N/A"}
                      </td>
                      <td>{formatDateTime(item?.created)}</td>
                      <td className="tablehead">
                        <button
                          className="eye-button"
                          onClick={() => handleViewProducts(item)}
                        >
                          <Eye size={20} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center">
                      No transactions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pagination
              lastPage={lastPage}
              currentPage={currentPage}
              scrollTargetRef={sectionTopRef}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target.className === "modal-overlay") {
              setShowModal(false);
            }
          }}
        >
          <div
            ref={modalRef}
            className={`modal-container ${isDarkMode ? "modal-dark" : "modal-light"}`}
            style={{
              transform: `translateY(${dragPosition}px)`,
              transition: isDragging ? "none" : "transform 0.3s ease",
              width:
                selectedProducts?.type === "Battle"
                  ? isMobile
                    ? "100%"
                    : "30%"
                  : "100%",
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Drag Handle - Mobile Only */}
            <div className="modal-drag-handle">
              <div className="drag-bar"></div>
            </div>

            {/* Modal Header */}
            <div className="modal-header-custom">
              <h5 className="modal-title-custom">{selectedProducts?.type}</h5>
              <button
                className="modal-close-btn"
                onClick={() => setShowModal(false)}
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="modal-body-custom">{renderModalBody()}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default GameHistory;
